import Button from '@mui/material/Button';
import IconButton from '@mui/material/IconButton';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material';
import { TimePicker } from '@mui/x-date-pickers';
import moment from 'moment';
import { useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { RouteOpeningHour, RouteScheduleResource } from '~/generated';
import { AxiosError } from 'axios';
import Inline from '~/modules/ui-kit/spacings/inline';
import Add from '@mui/icons-material/Add';
import Delete from '@mui/icons-material/Delete';
import { Box } from '@mui/system';
import messages from './messages';
import { useAppForm } from '~/components/Form';

type Weekday =
  | 'monday'
  | 'tuesday'
  | 'wednesday'
  | 'thursday'
  | 'friday'
  | 'saturday'
  | 'sunday';

const weekdays: { key: Weekday; dayOfWeek: number }[] = [
  { key: 'monday', dayOfWeek: 1 },
  { key: 'tuesday', dayOfWeek: 2 },
  { key: 'wednesday', dayOfWeek: 3 },
  { key: 'thursday', dayOfWeek: 4 },
  { key: 'friday', dayOfWeek: 5 },
  { key: 'saturday', dayOfWeek: 6 },
  { key: 'sunday', dayOfWeek: 7 },
];

type RouteScheduleFormProps = {
  routeSchedules: RouteScheduleResource;
  onSubmit: (values: RouteScheduleResource) => Promise<void>;
};

type FormValues = {
  monday: RouteOpeningHour[];
  tuesday: RouteOpeningHour[];
  wednesday: RouteOpeningHour[];
  thursday: RouteOpeningHour[];
  friday: RouteOpeningHour[];
  saturday: RouteOpeningHour[];
  sunday: RouteOpeningHour[];
};

const mapToWeekdays = (
  openingHours: RouteScheduleResource['openingHours']
) => ({
  monday: openingHours
    ? openingHours.filter((openingHour) => openingHour.dayOfWeek === 1)
    : [],
  tuesday: openingHours
    ? openingHours.filter((openingHour) => openingHour.dayOfWeek === 2)
    : [],
  wednesday: openingHours
    ? openingHours.filter((openingHour) => openingHour.dayOfWeek === 3)
    : [],
  thursday: openingHours
    ? openingHours.filter((openingHour) => openingHour.dayOfWeek === 4)
    : [],
  friday: openingHours
    ? openingHours.filter((openingHour) => openingHour.dayOfWeek === 5)
    : [],
  saturday: openingHours
    ? openingHours.filter((openingHour) => openingHour.dayOfWeek === 6)
    : [],
  sunday: openingHours
    ? openingHours.filter((openingHour) => openingHour.dayOfWeek === 7)
    : [],
});

const mapWeekdaysToRouteScheduleResource = (
  weekdayValues: FormValues,
  routeSchedules: RouteScheduleResource
) => ({
  ...routeSchedules,
  openingHours: Object.values(weekdayValues).flat(),
});

const RouteScheduleForm = (props: RouteScheduleFormProps) => {
  const intl = useIntl();
  const theme = useTheme();
  const [rootError, setRootError] = useState<string | null>(null);
  const form = useAppForm({
    defaultValues: mapToWeekdays(props.routeSchedules.openingHours),
    onSubmit: async ({ value }) => {
      try {
        setRootError(null);
        await props.onSubmit(
          mapWeekdaysToRouteScheduleResource(value, props.routeSchedules)
        );
      } catch (error) {
        if (error instanceof AxiosError) {
          if (error.response?.data) {
            setRootError(
              // eslint-disable-next-line @typescript-eslint/no-explicit-any
              (error.response.data as any).errors._error.join('; ')
            );
          }
        }
      }
    },
  });

  return (
    <Box sx={{ p: 3 }}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          e.stopPropagation();
          form.handleSubmit();
        }}
      >
        <Stack spacing={3}>
          <Typography variant="h6">
            <FormattedMessage {...messages.routeSchedule} />
          </Typography>
          <Typography variant="body2" color="GrayText">
            <FormattedMessage {...messages.featureDescription} />
          </Typography>
          {rootError ? (
            <Typography color={theme.palette.error.main}>
              {rootError}
            </Typography>
          ) : null}
          {weekdays.map(({ key, dayOfWeek }) => (
            <form.Field key={key} name={key} mode="array">
              {(arrayField) => (
                <div>
                  <Stack spacing={2}>
                    <Typography variant="subtitle2">
                      {moment().isoWeekday(dayOfWeek).format('dddd')}
                    </Typography>
                    {arrayField.state.value.map(
                      (_timeSlot: RouteOpeningHour, index: number) => (
                        <Inline alignItems="center" key={`${key}-${index}`}>
                          <form.Field name={`${key}[${index}].from`}>
                            {(field) => (
                              <TimePicker
                                label={<FormattedMessage {...messages.from} />}
                                value={moment(
                                  field.state.value as string,
                                  'HH:mm:SS'
                                )}
                                onChange={(date) =>
                                  field.handleChange(
                                    date?.format('HH:mm:SS') || ''
                                  )
                                }
                              />
                            )}
                          </form.Field>
                          <div>-</div>
                          <form.Field name={`${key}[${index}].until`}>
                            {(field) => (
                              <TimePicker
                                label={<FormattedMessage {...messages.until} />}
                                value={moment(
                                  field.state.value as string,
                                  'HH:mm:SS'
                                )}
                                onChange={(date) =>
                                  field.handleChange(
                                    date?.format('HH:mm:SS') || ''
                                  )
                                }
                              />
                            )}
                          </form.Field>
                          <IconButton
                            aria-label={intl.formatMessage(
                              messages.removeOpeningHours
                            )}
                            onClick={() => arrayField.removeValue(index)}
                          >
                            <Delete />
                          </IconButton>
                        </Inline>
                      )
                    )}
                    {arrayField.state.value.length === 0 ? (
                      <Typography variant="body2" color="GrayText">
                        <FormattedMessage
                          {...messages.noOpeningHoursForThisDay}
                        />
                      </Typography>
                    ) : null}
                    <div>
                      <Button
                        startIcon={<Add />}
                        variant="outlined"
                        onClick={() =>
                          arrayField.pushValue({
                            dayOfWeek,
                          } as RouteOpeningHour)
                        }
                      >
                        <FormattedMessage {...messages.addOpeningHours} />
                      </Button>
                    </div>
                  </Stack>
                </div>
              )}
            </form.Field>
          ))}
          <div>
            <Button type="submit" variant="contained" size="large">
              <FormattedMessage {...messages.save} />
            </Button>
          </div>
        </Stack>
      </form>
    </Box>
  );
};

export default RouteScheduleForm;
